import { useMemo, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

export type CustomerFormSeed = {
  id?: string
  name?: string | null
  phone?: string | null
  email?: string | null
  address?: string | null
  notes?: string | null
  preferred_contact?: string | null
}

type SaveResponse = { id: string }

const PREFERRED_OPTIONS = ['sms', 'email', 'phone', ''] as const

// The form body — owns local state, the create/update mutation, and the
// fields. Rendered inside CustomerDialog for inline edits, and on its
// own page (/customers/new → src/pages/NewCustomer.tsx) so the create
// flow gets full vertical room and a routable URL.
export function CustomerForm({
  seed,
  onSaved,
  onCancel,
  autoFocus = true,
}: {
  seed?: CustomerFormSeed | null
  onSaved?: (id: string) => void
  onCancel?: () => void
  autoFocus?: boolean
}) {
  const { t } = useTranslation('customers')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  const editing = !!seed?.id

  const [name, setName] = useState(seed?.name ?? '')
  const [phone, setPhone] = useState(seed?.phone ?? '')
  const [email, setEmail] = useState(seed?.email ?? '')
  const [address, setAddress] = useState(seed?.address ?? '')
  const [notes, setNotes] = useState(seed?.notes ?? '')
  const [preferred, setPreferred] = useState<string>(seed?.preferred_contact ?? '')
  const [error, setError] = useState<string | null>(null)

  // Reseed when the seed identity flips (e.g. dialog reopens on a
  // different customer). React-idiomatic "adjust state on prop change":
  // store the last-applied seed id and rewrite all fields synchronously
  // during render when it shifts. React handles the in-render setState
  // by discarding the current paint and re-rendering with the new
  // values — no extra paint, no effect, no flicker.
  const seedId = seed?.id ?? null
  const [appliedSeedId, setAppliedSeedId] = useState(seedId)
  if (seedId !== appliedSeedId) {
    setAppliedSeedId(seedId)
    setName(seed?.name ?? '')
    setPhone(seed?.phone ?? '')
    setEmail(seed?.email ?? '')
    setAddress(seed?.address ?? '')
    setNotes(seed?.notes ?? '')
    setPreferred(seed?.preferred_contact ?? '')
    setError(null)
  }

  const mutation = useMutation({
    mutationFn: async (): Promise<SaveResponse> => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const trimmedName = name.trim()
      if (!trimmedName) {
        throw new Error(t('form.error_name_required'))
      }
      const trimmedEmail = email.trim()
      if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        throw new Error(t('form.error_email_invalid'))
      }

      const payload = {
        name: trimmedName,
        phone: phone.trim() || null,
        email: trimmedEmail || null,
        address: address.trim() || null,
        notes: notes.trim() || null,
        preferred_contact: preferred || null,
      }

      const url = editing ? `/api/customers/${seed!.id}` : '/api/customers'
      const method = editing ? 'PATCH' : 'POST'
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || t('form.error_submit'))
      }
      return (await res.json()) as SaveResponse
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      if (editing && seed?.id) {
        queryClient.invalidateQueries({ queryKey: ['customer', seed.id] })
      }
      onSaved?.(data.id)
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : String(err))
    },
  })

  const submitting = mutation.isPending
  const canSubmit = useMemo(() => name.trim().length > 0 && !submitting, [
    name,
    submitting,
  ])

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!canSubmit) return
        setError(null)
        mutation.mutate()
      }}
      className="space-y-4"
    >
      <div className="space-y-1.5">
        <Label htmlFor="customer-name">{t('form.fields.name')}</Label>
        <Input
          id="customer-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('form.fields.name_placeholder')}
          disabled={submitting}
          autoFocus={autoFocus}
          required
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="customer-phone" className="flex items-baseline gap-2">
            {t('form.fields.phone')}
            <span className="text-xs font-normal text-muted-foreground">
              {tCommon('optional')}
            </span>
          </Label>
          <Input
            id="customer-phone"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('form.fields.phone_placeholder')}
            disabled={submitting}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="customer-email" className="flex items-baseline gap-2">
            {t('form.fields.email')}
            <span className="text-xs font-normal text-muted-foreground">
              {tCommon('optional')}
            </span>
          </Label>
          <Input
            id="customer-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('form.fields.email_placeholder')}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="customer-address" className="flex items-baseline gap-2">
          {t('form.fields.address')}
          <span className="text-xs font-normal text-muted-foreground">
            {tCommon('optional')}
          </span>
        </Label>
        <Input
          id="customer-address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={t('form.fields.address_placeholder')}
          disabled={submitting}
        />
      </div>

      <div className="space-y-1.5">
        <Label>{t('form.fields.preferred_contact')}</Label>
        <div className="flex flex-wrap gap-2">
          {PREFERRED_OPTIONS.map((opt) => {
            const isActive = preferred === opt
            return (
              <button
                key={opt || 'none'}
                type="button"
                onClick={() => setPreferred(opt)}
                disabled={submitting}
                className={`min-h-11 rounded-full border px-4 text-sm font-[600] transition-[background-color,border-color,color,transform] [transition-duration:120ms] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 ${
                  isActive
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background text-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:border-foreground/30'
                }`}
              >
                {opt === ''
                  ? t('form.preferred.none')
                  : t(`form.preferred.${opt}` as const)}
              </button>
            )
          })}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="customer-notes">{t('form.fields.notes')}</Label>
        <Textarea
          id="customer-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('form.fields.notes_placeholder')}
          rows={3}
          disabled={submitting}
        />
      </div>

      {error ? (
        <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive">
          {error}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-4 flex justify-end gap-2 border-t bg-background px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:static sm:mx-0 sm:border-t-0 sm:bg-transparent sm:px-0 sm:py-0">
        {onCancel ? (
          <Button
            type="button"
            variant="outline"
            disabled={submitting}
            onClick={onCancel}
          >
            {t('form.cancel')}
          </Button>
        ) : null}
        <Button type="submit" disabled={!canSubmit}>
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t('form.saving')}
            </>
          ) : editing ? (
            t('form.save_edit')
          ) : (
            t('form.save_new')
          )}
        </Button>
      </div>
    </form>
  )
}

export function CustomerDialog({
  open,
  onOpenChange,
  seed,
  onSaved,
}: {
  open: boolean
  onOpenChange: (next: boolean) => void
  seed?: CustomerFormSeed | null
  onSaved?: (id: string) => void
}) {
  const { t } = useTranslation('customers')
  const editing = !!seed?.id

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? t('form.title_edit') : t('form.title_new')}
          </DialogTitle>
          <DialogDescription>
            {editing ? t('form.subtitle_edit') : t('form.subtitle_new')}
          </DialogDescription>
        </DialogHeader>

        <CustomerForm
          seed={seed}
          onSaved={(id) => {
            onSaved?.(id)
            onOpenChange(false)
          }}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}
